import { useState, useEffect, useRef } from "react";
import apiService from "../services/api";

// ═══════════════════════════════════════════════════════════════════
//  L E X S O V E R E I G N  v3.1 - Con IA Integrada
//  Powered by Claude Sonnet 4 (Emergent LLM Key)
// ═══════════════════════════════════════════════════════════════════

const FONT_URL = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&family=JetBrains+Mono:wght@300;400;600&family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&display=swap";

const EC = { CRÍTICO:"#E53E3E", URGENTE:"#DD6B20", ESTRUCTURAL:"#805AD5", GESTIONABLE:"#38A169", MODERADO:"#3182CE", BAJO:"#718096" };
const fmt = n => n >= 1 ? `$${n.toFixed(1)}B` : `$${(n*1000).toFixed(0)}M`;

// ── MINI COMPONENTS ───────────────────────────────────────────────
const Tag = ({label, color="#C8963E", style={}}) => (
  <span style={{background:`${color}18`,border:`1px solid ${color}44`,color,fontSize:8,letterSpacing:1.5,padding:"2px 7px",borderRadius:1,fontFamily:"var(--fm)",textTransform:"uppercase",whiteSpace:"nowrap",...style}}>{label}</span>
);

const Dot = ({color, pulse=false}) => (
  <div style={{width:6,height:6,borderRadius:"50%",background:color,boxShadow:`0 0 6px ${color}`,flexShrink:0}} className={pulse?"pulse":""} />
);

const Metric = ({label,value,color="#C8963E",sub,small=false}) => (
  <div style={{background:"var(--glass)",border:`1px solid ${color}22`,borderTop:`2px solid ${color}`,borderRadius:2,padding:small?"10px 12px":"13px 16px"}}>
    <div style={{color:"var(--tx3)",fontSize:8,letterSpacing:2,fontFamily:"var(--fm)",marginBottom:5}}>{label}</div>
    <div style={{color,fontSize:small?18:22,fontWeight:700,fontFamily:"var(--fm)",lineHeight:1}}>{value}</div>
    {sub && <div style={{color:"var(--tx3)",fontSize:9,marginTop:4,fontFamily:"var(--fm)"}}>{sub}</div>}
  </div>
);

// ══════════════════════════════════════════════════════════════════
// CASO DETALLE
// ══════════════════════════════════════════════════════════════════
function CasoDetalle({caso, rol, rolInfo}) {
  if (!caso) return null;
  
  const acceso = getAcceso(caso, rol);
  const ec = EC[caso.estado] || "#C8963E";

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}} className="fu">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{color:"var(--tx3)",fontSize:8,letterSpacing:3,fontFamily:"var(--fm)",marginBottom:4}}>ANÁLISIS DE CASO</div>
          <h2 style={{color:"var(--tx)",fontFamily:"var(--fd)",fontSize:20,fontWeight:700,lineHeight:1.2}}>{caso.nombre}</h2>
          <div style={{color:"var(--tx3)",fontSize:11,fontFamily:"var(--fm)",marginTop:3}}>{caso.tribunal}</div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <Tag label={caso.estado} color={ec} />
          {caso.conflicto_skadden && <Tag label="⚠ CONFLICTO SKADDEN" color="var(--red)" />}
          {caso.firma_asignada && <Tag label={`▶ ${caso.firma_asignada}`} color="var(--ora)" />}
        </div>
      </div>

      {caso.conflicto_skadden && rol === "SKADDEN" && (
        <div style={{display:"flex",alignItems:"center",gap:6,background:"rgba(229,62,62,0.1)",border:"1px solid rgba(229,62,62,0.3)",borderRadius:2,padding:"5px 10px"}}>
          <Dot color="var(--red)" />
          <span style={{color:"var(--red)",fontSize:9,letterSpacing:1.5,fontFamily:"var(--fm)"}}>CONFLICTO DE INTERESES — ACCESO RESTRINGIDO A RESUMEN</span>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
        <Metric label="MONTO RECLAMADO" value={fmt(caso.monto)} color="var(--red)" sub={caso.fase} small />
        <Metric label="FECHA CRÍTICA" value={caso.fecha} color="var(--ora)" sub={`${caso.dias_restantes} días`} small />
        <Metric label="ACCESO ROL" value={acceso==="full"?"PLENO":acceso==="summary"?"RESUMEN":"DENEGADO"} color={acceso==="full"?"var(--grn)":acceso==="summary"?"var(--ora)":"var(--red)"} sub={rolInfo?.label} small />
      </div>

      {acceso === "summary" && (
        <div style={{background:"rgba(229,62,62,0.05)",border:"1px solid rgba(229,62,62,0.25)",borderRadius:2,padding:18}}>
          <div style={{color:"var(--red)",fontSize:9,letterSpacing:2,fontFamily:"var(--fm)",marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
            <Dot color="var(--red)" />
            RESUMEN EJECUTIVO — ACCESO SKADDEN (CONFLICTO DE INTERESES)
          </div>
          <p style={{color:"var(--tx)",fontSize:13,lineHeight:1.8,fontFamily:"var(--fb)"}}>{caso.resumen_ejecutivo}</p>
          <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid rgba(229,62,62,0.2)",color:"var(--tx3)",fontSize:10,fontFamily:"var(--fm)"}}>
            ⚠ El acceso completo está restringido. Firma alternativa: <span style={{color:"var(--ora)"}}>{caso.firma_alt}</span>
          </div>
        </div>
      )}

      {acceso === "full" && (
        <>
          <div style={{background:"var(--glass)",border:"1px solid var(--bd)",borderRadius:2,padding:16}}>
            <div style={{color:"var(--gold)",fontSize:8,letterSpacing:2,fontFamily:"var(--fm)",marginBottom:8}}>DESCRIPCIÓN DEL CASO</div>
            <p style={{color:"var(--tx)",fontSize:12,lineHeight:1.8,fontFamily:"var(--fb)"}}>{caso.descripcion}</p>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div style={{background:"rgba(229,62,62,0.05)",border:"1px solid rgba(229,62,62,0.2)",borderRadius:2,padding:14}}>
              <div style={{color:"var(--red)",fontSize:8,letterSpacing:2,fontFamily:"var(--fm)",marginBottom:10}}>FIRMAS CONTRAPARTE</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {caso.contraparte.map(f=><Tag key={f} label={f} color="var(--red)" style={{display:"inline-block",width:"fit-content"}} />)}
              </div>
            </div>
            <div style={{background:"rgba(56,161,105,0.05)",border:"1px solid rgba(56,161,105,0.2)",borderRadius:2,padding:14}}>
              <div style={{color:"var(--grn)",fontSize:8,letterSpacing:2,fontFamily:"var(--fm)",marginBottom:10}}>
                {caso.conflicto_skadden ? `RECOMENDACION — ${caso.firma_asignada?.toUpperCase()} / PROCURADURIA` : "RECOMENDACION ESTRATEGICA — ESCRITORIO SIN CONFLICTOS / PROCURADURIA"}
              </div>
              <p style={{color:"#A8F0C0",fontSize:12,lineHeight:1.7,fontFamily:"var(--fb)"}}>{caso.accion}</p>
            </div>
          </div>
        </>
      )}

      {acceso === "none" && (
        <div style={{textAlign:"center",padding:40,color:"var(--tx3)",fontFamily:"var(--fb)",fontStyle:"italic"}}>
          <div style={{fontSize:32,marginBottom:12,opacity:0.3}}>🔒</div>
          No tiene acceso asignado a este caso.
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// PANEL DOCUMENTOS CON IA
// ══════════════════════════════════════════════════════════════════
function PanelDocumentos({rol, casoActivo}) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    cargarDocumentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casoActivo, rol]);

  const cargarDocumentos = async () => {
    try {
      const documentos = await apiService.getDocumentos(rol, casoActivo?.id);
      setDocs(documentos);
    } catch (err) {
      console.error("Error cargando documentos:", err);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      const texto = await file.text();
      const documento = {
        nombre: file.name,
        caso_id: casoActivo?.id || null,
        caso_nombre: casoActivo?.nombre || "General",
        contenido: texto.slice(0, 5000), // Primeros 5000 caracteres
        uploaded_by: rol
      };

      const resultado = await apiService.analizarDocumento(documento, rol);
      setDocs([resultado, ...docs]);
    } catch (err) {
      console.error("Error analizando documento:", err);
      alert("Error al analizar documento");
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14,height:"100%"}}>
      <div style={{color:"var(--gold)",fontSize:9,letterSpacing:4,fontFamily:"var(--fm)"}}>
        ⊕ DOCUMENTOS — ANÁLISIS IA INSTANTÁNEO (CLAUDE SONNET 4)
        {casoActivo && <span style={{color:"var(--tx3)",letterSpacing:1,textTransform:"none"}}> · Caso: {casoActivo.nombre}</span>}
      </div>

      <div onClick={()=>fileRef.current?.click()} style={{border:"2px dashed var(--bd2)",borderRadius:2,padding:"22px",textAlign:"center",cursor:loading?"not-allowed":"pointer",background:loading?"rgba(200,150,62,0.05)":"var(--gdim)",transition:"all 0.2s",opacity:loading?0.6:1}}>
        <input ref={fileRef} type="file" accept=".txt,.md,.doc,.docx,.pdf" onChange={handleFile} disabled={loading} style={{display:"none"}} />
        <div style={{color:"var(--gold)",fontSize:24,marginBottom:6}}>⊕</div>
        <div style={{color:"var(--tx)",fontFamily:"var(--fd)",fontSize:13}}>{loading ? "Analizando..." : "Subir Documento Legal"}</div>
        <div style={{color:"var(--tx3)",fontSize:10,marginTop:3,fontFamily:"var(--fm)"}}>TXT · PDF · DOCX — Análisis IA en segundos</div>
      </div>

      <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:10}}>
        {docs.length===0 && !loading && <div style={{color:"var(--tx3)",textAlign:"center",marginTop:24,fontSize:12,fontStyle:"italic",fontFamily:"var(--fb)"}}>Los documentos analizados aparecerán aquí</div>}
        {docs.map(d=>(
          <div key={d.id} style={{background:"var(--glass)",border:"1px solid var(--bd)",borderRadius:2,padding:14}} className="fu">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{color:"var(--gold)"}}>📄</span>
                <span style={{color:"var(--tx)",fontSize:11,fontFamily:"var(--fm)"}}>{d.nombre}</span>
                {d.caso_nombre && <Tag label={d.caso_nombre} color="var(--blu)" />}
              </div>
              <Tag label={d.status==="procesando"?"ANALIZANDO":d.status==="listo"?"✓ LISTO":"ERROR"} color={d.status==="listo"?"var(--grn)":d.status==="procesando"?"var(--gold)":"var(--red)"} />
            </div>
            {d.status==="procesando" && <div style={{color:"var(--gold)",fontSize:10,fontFamily:"var(--fm)"}} className="pulse">◈ Claude Sonnet 4 analizando documento...</div>}
            {d.resumen_ia && (
              <div style={{background:"rgba(0,0,0,0.3)",borderRadius:2,padding:12,marginTop:6,borderLeft:"2px solid var(--grn)"}}>
                <div style={{color:"var(--grn)",fontSize:8,letterSpacing:2,fontFamily:"var(--fm)",marginBottom:6}}>▸ RESUMEN JURÍDICO IA</div>
                <div style={{color:"var(--tx)",fontSize:11,lineHeight:1.75,whiteSpace:"pre-wrap",fontFamily:"var(--fb)"}}>{d.resumen_ia}</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// PANEL ALERTAS
// ══════════════════════════════════════════════════════════════════
function PanelAlertas({rol, casos}) {
  const [alertas, setAlertas] = useState([]);

  useEffect(() => {
    cargarAlertas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rol]);

  const cargarAlertas = async () => {
    try {
      const data = await apiService.getAlertas(rol);
      setAlertas(data);
    } catch (err) {
      console.error("Error cargando alertas:", err);
    }
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{color:"var(--gold)",fontSize:9,letterSpacing:4,fontFamily:"var(--fm)"}}>⚠ ALERTAS & VENCIMIENTOS</div>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {alertas.map(a=>{
          const uc=EC[a.urgencia]||"var(--gold)";
          const caso=casos.find(c=>c.id===a.caso_id);
          return (
            <div key={a.id} style={{background:"var(--glass)",border:"1px solid var(--bd)",borderLeft:`3px solid ${uc}`,borderRadius:2,padding:"12px 16px",display:"flex",alignItems:"center",gap:16}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <Tag label={a.tipo} color={uc} />
                  {caso && <span style={{color:"var(--tx)",fontSize:11,fontFamily:"var(--fb)",fontWeight:700}}>{caso.nombre}</span>}
                </div>
                <div style={{color:"var(--tx2)",fontSize:11,fontFamily:"var(--fb)"}}>{a.detalle}</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{color:uc,fontFamily:"var(--fm)",fontSize:20,fontWeight:700,lineHeight:1}}>{a.dias}</div>
                <div style={{color:"var(--tx3)",fontSize:8,fontFamily:"var(--fm)"}}>DÍAS</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// PANEL COLABORACIÓN
// ══════════════════════════════════════════════════════════════════
function PanelColaboracion({rol, rolInfo, roles}) {
  const [msgs, setMsgs] = useState([]);
  const [nuevo, setNuevo] = useState("");

  useEffect(() => {
    cargarMensajes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rol]);

  const cargarMensajes = async () => {
    try {
      const data = await apiService.getMensajes(rol);
      setMsgs(data);
    } catch (err) {
      console.error("Error cargando mensajes:", err);
    }
  };

  const enviar = async () => {
    if(!nuevo.trim()) return;
    try {
      const hora = new Date().toLocaleTimeString("es-VE",{hour:"2-digit",minute:"2-digit"});
      const mensaje = {
        autor: rolInfo.label,
        rol,
        texto: nuevo,
        tipo: "nota"
      };
      const resultado = await apiService.createMensaje(mensaje, hora);
      setMsgs([...msgs, resultado]);
      setNuevo("");
    } catch (err) {
      console.error("Error enviando mensaje:", err);
    }
  };

  const rc = r => roles[r]?.color || "var(--tx2)";

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",gap:12}}>
      <div style={{color:"var(--gold)",fontSize:9,letterSpacing:4,fontFamily:"var(--fm)"}}>◆ COLABORACIÓN ENTRE FIRMAS — CANAL SEGURO</div>

      <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:8}}>
        {msgs.map(m=>(
          <div key={m.id} style={{borderRadius:2,padding:"10px 14px",borderLeft:`3px solid ${rc(m.rol)}`,background:"var(--glass)"}} className="fu">
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
              <span style={{color:rc(m.rol),fontSize:10,fontFamily:"var(--fm)",fontWeight:700}}>{m.autor}</span>
              <span style={{color:"var(--tx3)",fontSize:9,fontFamily:"var(--fm)"}}>{m.hora}</span>
            </div>
            <div style={{color:"var(--tx)",fontSize:12,lineHeight:1.65,fontFamily:"var(--fb)"}}>{m.texto}</div>
          </div>
        ))}
      </div>

      <div style={{display:"flex",gap:8}}>
        <textarea value={nuevo} onChange={e=>setNuevo(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&(e.preventDefault(),enviar())} placeholder={`Mensaje como ${rolInfo?.label}...`} rows={2}
          style={{flex:1,background:"rgba(0,0,0,0.4)",border:"1px solid var(--bd2)",borderRadius:2,color:"var(--tx)",padding:"9px 12px",fontSize:11,resize:"none"}} />
        <button onClick={enviar} style={{background:"linear-gradient(135deg,var(--gold),#7A5A1A)",border:"none",borderRadius:2,color:"#fff",padding:"9px 16px",fontSize:9,letterSpacing:2}}>▶ ENVIAR</button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// PANEL IA JURÍDICA CON CLAUDE
// ══════════════════════════════════════════════════════════════════
function PanelIA({rol, rolInfo, casos, casoActivo}) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [hist, setHist] = useState([]);

  const sugeridas = [
    "¿Cuál es la estructura de acceso actual de Skadden por caso?",
    "¿Qué casos tienen conflicto de intereses y quién los lleva?",
    "Resume las acciones urgentes de los próximos 30 días",
    "¿Cuál es la estrategia óptima para ConocoPhillips?",
  ];

  const consultar = async (texto) => {
    const query = texto||q;
    if(!query.trim()||loading) return;
    setQ(""); 
    setLoading(true);

    const nuevoHist=[...hist,{role:"user",content:query}];
    setHist(nuevoHist);

    try {
      const respuesta = await apiService.consultaIA(query, rol, casoActivo?.id);
      setHist([...nuevoHist,{role:"assistant",content:respuesta}]);
    } catch (err) {
      console.error("Error en consulta IA:", err);
      setHist([...nuevoHist,{role:"assistant",content:"Error en consulta. Intente nuevamente."}]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",gap:12}}>
      <div style={{color:"var(--gold)",fontSize:9,letterSpacing:4,fontFamily:"var(--fm)"}}>◇ CONSULTA IA JURÍDICA — CLAUDE SONNET 4</div>

      {hist.length===0 && (
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {sugeridas.map((s,i)=>(
            <button key={i} onClick={()=>consultar(s)} style={{background:"var(--gdim)",border:"1px solid var(--bd)",borderRadius:2,color:"var(--tx2)",padding:"8px 12px",fontSize:11,textAlign:"left",fontFamily:"var(--fb)",transition:"all 0.2s",cursor:"pointer"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor="var(--gold)";e.currentTarget.style.color="var(--tx)";}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--bd)";e.currentTarget.style.color="var(--tx2)";}}>
              › {s}
            </button>
          ))}
        </div>
      )}

      <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:10}}>
        {hist.map((m,i)=>(
          <div key={i} style={{alignSelf:m.role==="user"?"flex-end":"flex-start",maxWidth:"90%",background:m.role==="user"?"var(--gdim)":"rgba(8,20,50,0.8)",border:m.role==="user"?"1px solid var(--bd2)":"1px solid rgba(50,100,200,0.2)",borderRadius:2,padding:"10px 14px"}}>
            {m.role==="assistant"&&<div style={{color:"var(--gold)",fontSize:8,letterSpacing:2,fontFamily:"var(--fm)",marginBottom:6}}>▸ ANÁLISIS CLAUDE SONNET 4</div>}
            <div style={{color:m.role==="user"?"var(--gold2)":"var(--tx)",fontSize:12,lineHeight:1.7,whiteSpace:"pre-wrap",fontFamily:"var(--fb)"}}>{m.content}</div>
          </div>
        ))}
        {loading&&<div style={{color:"var(--gold)",fontSize:10,fontFamily:"var(--fm)"}} className="pulse">◈ Claude analizando portafolio soberano...</div>}
      </div>

      <div style={{display:"flex",gap:8}}>
        <input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==="Enter"&&consultar()} placeholder="Consulte sobre el portafolio..." style={{flex:1,background:"rgba(0,0,0,0.4)",border:"1px solid var(--bd2)",borderRadius:2,color:"var(--tx)",padding:"10px 14px",fontSize:12}} />
        <button onClick={()=>consultar()} disabled={loading} style={{background:loading?"rgba(200,150,62,0.1)":"linear-gradient(135deg,var(--gold),#7A5A1A)",border:"none",borderRadius:2,color:"#fff",padding:"10px 18px",fontSize:9,letterSpacing:2,opacity:loading?0.5:1,cursor:loading?"not-allowed":"pointer"}}>▶</button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// PANEL ACTUALIZACIONES AUTOMÁTICAS CON IA
// ══════════════════════════════════════════════════════════════════
function PanelActualizacionesIA({rol, rolInfo, casos}) {
  const [tipo, setTipo] = useState("caso_update");
  const [textoLibre, setTextoLibre] = useState("");
  const [casoSeleccionado, setCasoSeleccionado] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendientes, setPendientes] = useState([]);
  const [mostrarPendientes, setMostrarPendientes] = useState(false);

  const puedeEnviar = ["LATHAM", "MGO", "SKADDEN", "PROCURADURIA"].includes(rol);
  const puedeAprobar = ["PRESIDENCIA", "PROCURADURIA"].includes(rol);

  useEffect(() => {
    if (puedeAprobar) {
      cargarPendientes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rol]);

  const cargarPendientes = async () => {
    try {
      const data = await apiService.getActualizacionesPendientes(rol);
      setPendientes(data);
    } catch (err) {
      console.error("Error cargando pendientes:", err);
    }
  };

  const enviarActualizacion = async () => {
    if (!textoLibre.trim()) return;
    setLoading(true);

    try {
      const resultado = await apiService.submitActualizacion(
        tipo,
        textoLibre,
        casoSeleccionado || null,
        rol
      );

      alert(`✓ Actualización procesada!\n\nAnálisis IA:\n${resultado.ia_analisis?.substring(0, 200)}...`);
      
      setTextoLibre("");
      setCasoSeleccionado("");
      
      if (puedeAprobar) {
        cargarPendientes();
      }
    } catch (err) {
      console.error("Error:", err);
      alert("Error al procesar actualización");
    } finally {
      setLoading(false);
    }
  };

  const aprobar = async (updateId, aprobado) => {
    try {
      await apiService.aprobarActualizacion(updateId, aprobado, null, rol);
      alert(aprobado ? "✓ Actualización aprobada y aplicada" : "✗ Actualización rechazada");
      cargarPendientes();
    } catch (err) {
      console.error("Error:", err);
      alert("Error al procesar aprobación");
    }
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14,height:"100%"}}>
      <div style={{color:"var(--gold)",fontSize:9,letterSpacing:4,fontFamily:"var(--fm)"}}>
        🤖 ACTUALIZACIONES AUTOMÁTICAS CON IA — CLAUDE SONNET 4
      </div>

      {puedeAprobar && (
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>setMostrarPendientes(!mostrarPendientes)} style={{background:mostrarPendientes?"var(--gold)":"transparent",border:"1px solid var(--gold)",color:mostrarPendientes?"#000":"var(--gold)",borderRadius:2,padding:"8px 16px",fontSize:10,letterSpacing:1,cursor:"pointer"}}>
            {mostrarPendientes?"NUEVA ACTUALIZACIÓN":"VER PENDIENTES"} ({pendientes.length})
          </button>
        </div>
      )}

      {!mostrarPendientes ? (
        <>
          {puedeEnviar ? (
            <>
              <div style={{background:"rgba(56,161,105,0.05)",border:"1px solid rgba(56,161,105,0.2)",borderRadius:2,padding:14}}>
                <div style={{color:"var(--grn)",fontSize:10,letterSpacing:1.5,fontFamily:"var(--fm)",marginBottom:8}}>
                  ✓ CÓMO FUNCIONA EL SISTEMA AUTO-UPDATE
                </div>
                <ul style={{color:"var(--tx2)",fontSize:11,lineHeight:1.7,fontFamily:"var(--fb)",marginLeft:20}}>
                  <li>Escribe la actualización en lenguaje natural</li>
                  <li>Claude Sonnet 4 analiza y estructura automáticamente</li>
                  <li>Procuraduría/Presidencia revisa y aprueba</li>
                  <li>Sistema aplica cambios inmediatamente</li>
                </ul>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10}}>
                <div>
                  <div style={{color:"var(--tx3)",fontSize:9,marginBottom:6,fontFamily:"var(--fm)"}}>TIPO DE ACTUALIZACIÓN</div>
                  <select value={tipo} onChange={e=>setTipo(e.target.value)} style={{width:"100%",background:"rgba(0,0,0,0.4)",border:"1px solid var(--bd2)",borderRadius:2,color:"var(--tx)",padding:"8px 12px",fontSize:11}}>
                    <option value="caso_update">Actualizar Caso Existente</option>
                    <option value="nuevo_caso">Nuevo Caso</option>
                    <option value="alerta">Nueva Alerta</option>
                    <option value="conflicto">Reportar Conflicto</option>
                  </select>
                </div>

                {tipo === "caso_update" && (
                  <div>
                    <div style={{color:"var(--tx3)",fontSize:9,marginBottom:6,fontFamily:"var(--fm)"}}>CASO A ACTUALIZAR</div>
                    <select value={casoSeleccionado} onChange={e=>setCasoSeleccionado(e.target.value)} style={{width:"100%",background:"rgba(0,0,0,0.4)",border:"1px solid var(--bd2)",borderRadius:2,color:"var(--tx)",padding:"8px 12px",fontSize:11}}>
                      <option value="">Seleccionar caso...</option>
                      {casos.map(c=><option key={c.id} value={c.id}>{c.nombre}</option>)}
                    </select>
                  </div>
                )}
              </div>

              <div>
                <div style={{color:"var(--tx3)",fontSize:9,marginBottom:6,fontFamily:"var(--fm)"}}>INFORMACIÓN (Lenguaje Natural)</div>
                <textarea value={textoLibre} onChange={e=>setTextoLibre(e.target.value)} placeholder="Ej: ConocoPhillips propone pago diferido 15 años con petróleo. Monto reduciría a USD 7B. Nuevo vencimiento 30 abril 2026..." rows={5}
                  style={{width:"100%",background:"rgba(0,0,0,0.4)",border:"1px solid var(--bd2)",borderRadius:2,color:"var(--tx)",padding:"12px",fontSize:12,resize:"none",lineHeight:1.6,fontFamily:"var(--fb)"}} />
              </div>

              <button onClick={enviarActualizacion} disabled={loading||!textoLibre.trim()} style={{background:loading?"rgba(200,150,62,0.1)":"linear-gradient(135deg,var(--gold),#7A5A1A)",border:"none",borderRadius:2,color:"#fff",padding:"12px 20px",fontSize:10,letterSpacing:2,opacity:loading?0.6:1,cursor:loading?"not-allowed":"pointer",fontFamily:"var(--fm)"}}>
                {loading?"🤖 PROCESANDO CON IA...":"🤖 ENVIAR A IA PARA ANÁLISIS"}
              </button>
            </>
          ) : (
            <div style={{textAlign:"center",padding:40,color:"var(--tx3)",fontFamily:"var(--fb)",fontStyle:"italic"}}>
              <div style={{fontSize:32,marginBottom:12,opacity:0.3}}>🔒</div>
              Solo Procuraduría puede enviar actualizaciones al sistema.
            </div>
          )}
        </>
      ) : (
        <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:10}}>
          <div style={{color:"var(--ora)",fontSize:10,letterSpacing:1.5,fontFamily:"var(--fm)",marginBottom:4}}>
            ⚠ ACTUALIZACIONES PENDIENTES DE APROBACIÓN
          </div>

          {pendientes.length === 0 ? (
            <div style={{textAlign:"center",padding:40,color:"var(--tx3)",fontFamily:"var(--fb)",fontStyle:"italic"}}>
              No hay actualizaciones pendientes
            </div>
          ) : (
            pendientes.map(p=>(
              <div key={p.id} style={{background:"var(--glass)",border:"1px solid var(--bd)",borderRadius:2,padding:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <Tag label={p.tipo.replace("_"," ").toUpperCase()} color="var(--blu)" />
                    <span style={{color:"var(--tx3)",fontSize:9,fontFamily:"var(--fm)"}}>Por: {p.submitted_by}</span>
                  </div>
                  <Tag label={p.status} color={p.status==="procesado"?"var(--grn)":"var(--ora)"} />
                </div>

                <div style={{background:"rgba(0,0,0,0.3)",borderRadius:2,padding:10,marginBottom:10}}>
                  <div style={{color:"var(--tx3)",fontSize:8,letterSpacing:1.5,fontFamily:"var(--fm)",marginBottom:4}}>TEXTO ORIGINAL:</div>
                  <div style={{color:"var(--tx2)",fontSize:11,lineHeight:1.6,fontFamily:"var(--fb)"}}>{p.texto_libre}</div>
                </div>

                {p.ia_analisis && (
                  <div style={{background:"rgba(56,161,105,0.05)",border:"1px solid rgba(56,161,105,0.2)",borderRadius:2,padding:10,marginBottom:10}}>
                    <div style={{color:"var(--grn)",fontSize:8,letterSpacing:1.5,fontFamily:"var(--fm)",marginBottom:6}}>🤖 ANÁLISIS IA:</div>
                    <div style={{color:"var(--tx)",fontSize:11,lineHeight:1.7,whiteSpace:"pre-wrap",fontFamily:"var(--fb)"}}>{p.ia_analisis}</div>
                  </div>
                )}

                <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                  <button onClick={()=>aprobar(p.id,false)} style={{background:"transparent",border:"1px solid var(--red)",color:"var(--red)",borderRadius:2,padding:"6px 14px",fontSize:9,letterSpacing:1,cursor:"pointer"}}>✗ RECHAZAR</button>
                  <button onClick={()=>aprobar(p.id,true)} style={{background:"linear-gradient(135deg,var(--grn),#2D7A55)",border:"none",borderRadius:2,color:"#fff",padding:"6px 14px",fontSize:9,letterSpacing:1,cursor:"pointer"}}>✓ APROBAR Y APLICAR</button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════
// MODAL BRIEFING CON IA
// ══════════════════════════════════════════════════════════════════
function ModalBriefing({rol, rolInfo, onClose}) {
  const [estado, setEstado] = useState("idle");
  const [briefing, setBriefing] = useState("");
  const [lang, setLang] = useState("es");

  const generar = async () => {
    setEstado("generando");
    try {
      const resultado = await apiService.generarBriefing(lang, "VEN", rol);
      setBriefing(resultado);
      setEstado("listo");
    } catch (err) {
      console.error("Error generando briefing:", err);
      setBriefing("Error al generar briefing.");
      setEstado("listo");
    }
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.88)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(10px)"}}>
      <div style={{background:"linear-gradient(160deg,#030810 0%,#060D1C 100%)",border:"1px solid var(--bd2)",borderRadius:3,width:"100%",maxWidth:740,maxHeight:"88vh",display:"flex",flexDirection:"column",boxShadow:"0 0 80px rgba(200,150,62,0.12)"}} className="fu">
        <div style={{padding:"16px 22px",borderBottom:"1px solid var(--bd)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{color:"var(--gold)",fontSize:8,letterSpacing:4,fontFamily:"var(--fm)"}}>LEXSOVEREIGN — BRIEFING EJECUTIVO IA</div>
            <div style={{color:"var(--tx)",fontFamily:"var(--fd)",fontSize:17,marginTop:2}}>República Bolivariana de Venezuela</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"1px solid var(--bd)",color:"var(--tx3)",borderRadius:2,width:30,height:30,fontSize:15,cursor:"pointer",position:"relative",zIndex:10}}>×</button>
        </div>

        <div style={{padding:"12px 22px",borderBottom:"1px solid var(--bd)",display:"flex",gap:10,alignItems:"center"}}>
          <div style={{display:"flex",gap:6}}>
            {[["es","Español"],["en","English"]].map(([l,lb])=>(
              <button key={l} onClick={()=>setLang(l)} style={{background:lang===l?"linear-gradient(135deg,var(--gold),#8A6A2A)":"transparent",border:"1px solid var(--bd2)",color:lang===l?"#fff":"var(--tx3)",borderRadius:2,padding:"5px 14px",fontSize:10,letterSpacing:1,cursor:"pointer"}}>{lb}</button>
            ))}
          </div>
          <button onClick={generar} disabled={estado==="generando"} className={estado==="idle"?"glow":""} style={{marginLeft:"auto",background:estado==="generando"?"rgba(200,150,62,0.1)":"linear-gradient(135deg,var(--gold),#7A5A1A)",border:"none",borderRadius:2,color:"#fff",padding:"8px 20px",fontSize:9,letterSpacing:2,opacity:estado==="generando"?0.7:1,cursor:estado==="generando"?"not-allowed":"pointer"}}>
            {estado==="generando"?"◈ GENERANDO CON IA...":"◈ GENERAR BRIEFING"}
          </button>
          {estado==="listo"&&<button onClick={()=>navigator.clipboard?.writeText(briefing)} style={{background:"transparent",border:"1px solid var(--grn)",color:"var(--grn)",borderRadius:2,padding:"8px 14px",fontSize:9,letterSpacing:1,cursor:"pointer"}}>⎘ COPIAR</button>}
        </div>

        <div style={{flex:1,overflowY:"auto",padding:"18px 22px"}}>
          {estado==="idle"&&<div style={{textAlign:"center",color:"var(--tx3)",marginTop:36,fontFamily:"var(--fb)",fontStyle:"italic"}}>
            <div style={{fontSize:30,marginBottom:10,color:"var(--gold)",opacity:0.35}}>◈</div>
            Seleccione idioma y presione Generar Briefing<br/>
            <span style={{fontSize:11}}>Claude Sonnet 4 consolidará todos los casos y producirá el informe presidencial</span>
          </div>}
          {estado==="generando"&&<div style={{textAlign:"center",color:"var(--gold)",marginTop:36,fontFamily:"var(--fm)"}} className="pulse">
            <div style={{fontSize:30,marginBottom:10}}>◈</div>
            Claude Sonnet 4 consolidando portafolio soberano...<br/>
            <span style={{fontSize:10,color:"var(--tx3)",display:"block",marginTop:6}}>8 casos · USD 170.4B exposición total</span>
          </div>}
          {estado==="listo"&&briefing&&<div style={{background:"rgba(0,0,0,0.3)",border:"1px solid var(--bd)",borderLeft:"3px solid var(--gold)",borderRadius:2,padding:18,color:"var(--tx)",fontSize:12,lineHeight:1.9,whiteSpace:"pre-wrap",fontFamily:"var(--fm)"}} className="fu">{briefing}</div>}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ACCESS CONTROL
// ══════════════════════════════════════════════════════════════════
function getAcceso(caso, rol) {
  if (["PRESIDENCIA", "PROCURADURIA", "MGO"].includes(rol)) return "full";
  
  // Latham & Watkins: solo casos asignados
  if (rol === "LATHAM") {
    return caso.firma_asignada === "Latham & Watkins" ? "full" : "none";
  }
  
  // Skadden: resumen en conflictos, pleno en otros
  if (rol === "SKADDEN") {
    return caso.conflicto_skadden ? "summary" : "full";
  }
  
  return "none";
}

// ══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════
export default function LexSovereign({ session, onLogout }) {
  const [casos, setCasos] = useState([]);
  const [casoActivo, setCasoActivo] = useState(null);
  const [panel, setPanel] = useState("dashboard");
  const [showBriefing, setShowBriefing] = useState(false);
  const [hora, setHora] = useState(new Date());
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setInterval(() => setHora(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.rol]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [casosData, rolesData] = await Promise.all([
        apiService.getCasos(session.rol),
        apiService.getRoles()
      ]);
      setCasos(casosData);
      setRoles(rolesData);
    } catch (err) {
      console.error("Error cargando datos:", err);
    } finally {
      setLoading(false);
    }
  };

  const casosVisibles = casos.filter(c => getAcceso(c, session.rol) !== "none");
  const totalExp = casosVisibles.reduce((a, c) => a + c.monto, 0);
  const criticos = casosVisibles.filter(c => c.estado === "CRÍTICO" || c.estado === "URGENTE").length;
  const conConflicto = casosVisibles.filter(c => c.conflicto_skadden).length;

  const PTABS = [
    { k: "dashboard", l: "◈ Dashboard" },
    { k: "documentos", l: "⊕ Documentos IA" },
    { k: "alertas", l: "⚠ Alertas" },
    { k: "colaboracion", l: "◆ Colaboración" },
    { k: "ia", l: "◇ IA Jurídica" },
    { k: "autoupdate", l: "🤖 Auto-Update IA" },
  ];

  if (loading) {
    return (
      <div style={{height:"100vh",background:"var(--navy)",display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div style={{color:"var(--gold)",fontSize:14,fontFamily:"var(--fm)"}} className="pulse">◈ Cargando LexSovereign...</div>
      </div>
    );
  }

  return (
    <div style={{height:"100vh",background:"var(--navy)",color:"var(--tx)",display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <style>{CSS}</style>

      <div style={{position:"fixed",inset:0,background:"repeating-linear-gradient(0deg,transparent,transparent 3px,rgba(0,15,40,0.025) 3px,rgba(0,15,40,0.025) 4px)",pointerEvents:"none",zIndex:0}} />

      <div style={{position:"relative",zIndex:1,display:"flex",flexDirection:"column",height:"100%"}}>

        {/* TOPBAR */}
        <div style={{borderBottom:"1px solid var(--bd)",display:"flex",alignItems:"stretch",height:50,flexShrink:0,background:"rgba(3,8,16,0.95)"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"0 18px",borderRight:"1px solid var(--bd)"}}>
            <div style={{width:26,height:26,border:"1px solid var(--gold)",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--gold)",fontSize:13}}>⚖</div>
            <div>
              <div style={{color:"var(--gold)",fontSize:12,fontFamily:"var(--fd)",fontWeight:700,letterSpacing:2,lineHeight:1}}>LEX<span style={{color:"var(--tx)"}}>SOVEREIGN</span></div>
              <div style={{color:"var(--tx3)",fontSize:7,fontFamily:"var(--fm)",letterSpacing:2}}>GLOBAL LEGAL INTELLIGENCE v3.1</div>
            </div>
          </div>

          <div style={{flex:1,display:"flex",alignItems:"center",padding:"0 16px"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <Dot color={session.color} />
              <span style={{color:session.color,fontSize:10,fontFamily:"var(--fm)",letterSpacing:1}}>{session.label}</span>
            </div>
          </div>

          <div style={{display:"flex",alignItems:"center",gap:12,padding:"0 16px",borderLeft:"1px solid var(--bd)"}}>
            <div style={{color:"var(--tx3)",fontSize:9,fontFamily:"var(--fm)",textAlign:"right"}}>
              <div style={{fontSize:11,color:"var(--tx2)"}}>{hora.toLocaleTimeString("es-VE")}</div>
              <div>{hora.toLocaleDateString("es-VE",{day:"2-digit",month:"short"})}</div>
            </div>
            <button onClick={onLogout} style={{background:"transparent",border:"1px solid var(--bd)",color:"var(--tx3)",borderRadius:2,padding:"4px 10px",fontSize:9,cursor:"pointer"}}>SALIR</button>
          </div>
        </div>

        {/* BODY */}
        <div style={{flex:1,display:"flex",overflow:"hidden"}}>

          {/* SIDEBAR */}
          <div style={{width:232,borderRight:"1px solid var(--bd)",display:"flex",flexDirection:"column",flexShrink:0,background:"rgba(3,8,16,0.6)"}}>
            <div style={{padding:"12px 12px 10px",borderBottom:"1px solid var(--bd)",display:"flex",flexDirection:"column",gap:6}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                <Metric label="EXPOSICIÓN" value={fmt(totalExp)} color="var(--red)" small />
                <Metric label="CRÍTICOS" value={criticos} color="var(--ora)" small />
              </div>
              {conConflicto>0 && (
                <div style={{background:"rgba(229,62,62,0.08)",border:"1px solid rgba(229,62,62,0.25)",borderRadius:2,padding:"6px 10px",display:"flex",alignItems:"center",gap:6}}>
                  <Dot color="var(--red)" pulse />
                  <span style={{color:"var(--red)",fontSize:9,fontFamily:"var(--fm)",letterSpacing:1}}>{conConflicto} CON CONFLICTO SKADDEN</span>
                </div>
              )}
            </div>

            <div style={{flex:1,overflowY:"auto",padding:"8px 8px"}}>
              {casosVisibles.map(c=>{
                const ec=EC[c.estado]||"var(--gold)";
                const acc=getAcceso(c,session.rol);
                const sel=casoActivo?.id===c.id;
                return (
                  <div key={c.id} onClick={()=>{setCasoActivo(c);setPanel("dashboard");}}
                    style={{background:sel?"var(--gdim)":"transparent",border:sel?"1px solid var(--bd2)":"1px solid transparent",borderLeft:`3px solid ${c.conflicto_skadden?"var(--red)":ec}`,borderRadius:2,padding:"9px 10px",marginBottom:4,cursor:"pointer",transition:"all 0.15s"}}
                    onMouseEnter={e=>{if(!sel)e.currentTarget.style.background="rgba(200,150,62,0.05)";}}
                    onMouseLeave={e=>{if(!sel)e.currentTarget.style.background="transparent";}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:4,marginBottom:4}}>
                      <div style={{color:"var(--tx)",fontSize:10,fontFamily:"var(--fb)",fontWeight:700,lineHeight:1.3,flex:1}}>{c.nombre}</div>
                      {c.conflicto_skadden && <span title="Conflicto Skadden" style={{color:"var(--red)",fontSize:10,flexShrink:0}}>⚠</span>}
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <span style={{color:ec,fontFamily:"var(--fm)",fontSize:13,fontWeight:700}}>{fmt(c.monto)}</span>
                      <span style={{color:"var(--tx3)",fontSize:8,fontFamily:"var(--fm)"}}>{acc==="summary"?"RESUMEN":acc==="full"?"PLENO":"—"}</span>
                    </div>
                    {c.conflicto_skadden&&c.firma_asignada&&(
                      <div style={{color:"var(--ora)",fontSize:8,fontFamily:"var(--fm)",marginTop:3,letterSpacing:1}}>▶ {c.firma_asignada}</div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{padding:"10px 10px",borderTop:"1px solid var(--bd)"}}>
              <button onClick={()=>setShowBriefing(true)} className="glow" style={{width:"100%",background:"linear-gradient(135deg,var(--gold),#6A4A10)",border:"none",borderRadius:2,color:"#fff",padding:"11px",fontSize:9,letterSpacing:2,fontFamily:"var(--fm)",cursor:"pointer"}}>
                ◈ BRIEFING EJECUTIVO IA
              </button>
            </div>
          </div>

          {/* MAIN */}
          <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
            <div style={{display:"flex",borderBottom:"1px solid var(--bd)",height:38,flexShrink:0,background:"rgba(3,8,16,0.5)"}}>
              {PTABS.map(t=>(
                <button key={t.k} onClick={()=>setPanel(t.k)} style={{background:panel===t.k?"var(--gdim)":"transparent",border:"none",borderBottom:panel===t.k?"2px solid var(--gold)":"2px solid transparent",color:panel===t.k?"var(--gold)":"var(--tx3)",padding:"0 16px",fontSize:9,fontFamily:"var(--fm)",cursor:"pointer",letterSpacing:1.5,transition:"all 0.15s"}}>{t.l}</button>
              ))}
              {casoActivo && (
                <div style={{marginLeft:"auto",display:"flex",alignItems:"center",paddingRight:14,gap:8}}>
                  <span style={{color:"var(--tx2)",fontSize:9,fontFamily:"var(--fm)"}}>{casoActivo.id}</span>
                  <button onClick={()=>setCasoActivo(null)} style={{background:"none",border:"none",color:"var(--tx3)",fontSize:13,lineHeight:1,cursor:"pointer"}}>×</button>
                </div>
              )}
            </div>

            <div style={{flex:1,overflowY:"auto",padding:18}}>
              {panel==="dashboard" && (
                <div className="fu">
                  {!casoActivo ? (
                    <>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}}>
                        <Metric label="DEUDA SOBERANA TOTAL" value="$177.2B" color="var(--red)" sub="Incl. activos oro" />
                        <Metric label="LAUDOS ACTIVOS" value="$20B+" color="var(--ora)" sub="56 casos ICSID" />
                        <Metric label="CONFLICTOS SKADDEN" value={casos.filter(c=>c.conflicto_skadden).length} color="var(--red)" sub="firmas alt. designadas" />
                        <Metric label="CASOS VISIBLES" value={casosVisibles.length} color="var(--grn)" sub={`Rol: ${session.label}`} />
                      </div>

                      <div style={{background:"var(--glass)",border:"1px solid var(--bd)",borderRadius:2}}>
                        <div style={{padding:"10px 16px",borderBottom:"1px solid var(--bd)"}}>
                          <div style={{color:"var(--gold)",fontSize:8,letterSpacing:3,fontFamily:"var(--fm)"}}>PORTAFOLIO SOBERANO — TODOS LOS CASOS</div>
                        </div>
                        {casosVisibles.map((c,i)=>{
                          const ec=EC[c.estado]||"var(--gold)";
                          const acc=getAcceso(c,session.rol);
                          return (
                            <div key={c.id} onClick={()=>setCasoActivo(c)}
                              style={{padding:"11px 16px",borderBottom:i<casosVisibles.length-1?"1px solid var(--bd)":"none",display:"flex",alignItems:"center",gap:14,cursor:"pointer",transition:"background 0.15s"}}
                              onMouseEnter={e=>e.currentTarget.style.background="var(--gdim)"}
                              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                              <div style={{width:3,height:32,background:c.conflicto_skadden?"var(--red)":ec,borderRadius:2,flexShrink:0}} />
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{color:"var(--tx)",fontSize:11,fontFamily:"var(--fb)",fontWeight:700,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.nombre}</div>
                                <div style={{color:"var(--tx3)",fontSize:9,fontFamily:"var(--fm)",marginTop:1}}>
                                  {c.tribunal} · {c.conflicto_skadden?<span style={{color:"var(--ora)"}}>⚠ {c.firma_asignada}</span>:(c.firma_asignada||"Procuraduria")}
                                </div>
                              </div>
                              <Tag label={c.estado} color={ec} />
                              <Tag label={acc==="summary"?"RESUMEN":acc==="full"?"PLENO":"—"} color={acc==="full"?"var(--grn)":acc==="summary"?"var(--ora)":"var(--red)"} />
                              <div style={{color:ec,fontFamily:"var(--fm)",fontSize:15,fontWeight:700,width:72,textAlign:"right"}}>{fmt(c.monto)}</div>
                              <div style={{color:"var(--tx3)",fontSize:9,fontFamily:"var(--fm)",width:56,textAlign:"right"}}>{c.fecha}</div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <CasoDetalle caso={casoActivo} rol={session.rol} rolInfo={session} />
                  )}
                </div>
              )}

              {panel==="documentos" && <PanelDocumentos rol={session.rol} casoActivo={casoActivo} />}
              {panel==="alertas" && <PanelAlertas rol={session.rol} casos={casos} />}
              {panel==="colaboracion" && <PanelColaboracion rol={session.rol} rolInfo={session} roles={roles} />}
              {panel==="ia" && <PanelIA rol={session.rol} rolInfo={session} casos={casosVisibles} casoActivo={casoActivo} />}
              {panel==="autoupdate" && <PanelActualizacionesIA rol={session.rol} rolInfo={session} casos={casosVisibles} />}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div style={{borderTop:"1px solid var(--bd)",padding:"6px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0,background:"rgba(3,8,16,0.95)"}}>
          <div style={{color:"var(--tx3)",fontSize:7,letterSpacing:2,fontFamily:"var(--fm)"}}>CLASIFICADO — USO EXCLUSIVO PRESIDENCIA & PROCURADURÍA</div>
          <div style={{display:"flex",alignItems:"center",gap:6}}><Dot color="var(--grn)" pulse /><span style={{color:"var(--tx3)",fontSize:7,fontFamily:"var(--fm)"}}>IA ACTIVA — CLAUDE SONNET 4</span></div>
          <div style={{color:"var(--tx3)",fontSize:7,letterSpacing:2,fontFamily:"var(--fm)"}}>LEXSOVEREIGN v3.1 · EMERGENT LLM</div>
        </div>
      </div>

      {showBriefing && <ModalBriefing rol={session.rol} rolInfo={session} onClose={()=>setShowBriefing(false)} />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// CSS
// ══════════════════════════════════════════════════════════════════
const CSS = `@import url('${FONT_URL}'); *{box-sizing:border-box;margin:0;padding:0} :root{ --gold:#C8963E;--gold2:#E8B84B;--gdim:rgba(200,150,62,0.12); --navy:#030810;--n2:#060D1C;--n3:#091220; --glass:rgba(8,18,40,0.9); --bd:rgba(200,150,62,0.18);--bd2:rgba(200,150,62,0.38); --tx:#EDE0C4;--tx2:rgba(237,224,196,0.55);--tx3:rgba(237,224,196,0.3); --red:#E53E3E;--ora:#DD6B20;--grn:#38A169;--blu:#3182CE;--pur:#805AD5; --fd:'Playfair Display',Georgia,serif; --fb:'Libre Baskerville',Georgia,serif; --fm:'JetBrains Mono',monospace; } html,body,#root{height:100%;background:var(--navy)} ::-webkit-scrollbar{width:3px;height:3px} ::-webkit-scrollbar-track{background:rgba(0,0,0,0.3)} ::-webkit-scrollbar-thumb{background:var(--gold);border-radius:2px} input,textarea,select{outline:none;font-family:var(--fm)} input::placeholder,textarea::placeholder{color:var(--tx3)} button{cursor:pointer;font-family:var(--fm)} @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}} @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.35}} @keyframes glow{0%,100%{box-shadow:0 0 10px rgba(200,150,62,0.4)}50%{box-shadow:0 0 24px rgba(200,150,62,0.7),0 0 48px rgba(200,150,62,0.2)}} .fu{animation:fadeUp 0.35s ease forwards} .pulse{animation:pulse 1.4s infinite} .glow{animation:glow 2.5s ease-in-out infinite}`;
